import { useCallback, useEffect, useMemo, useState } from "preact/hooks";

import { loadManifest, StaleSchemaError } from "../manifest/load";
import { subscribe } from "../manifest/broadcast";
import { listArticles } from "../store/db";
import { ArticleCard } from "./ArticleCard";
import { ArticleDetailView } from "./ArticleDetail";
import { useBlockedPublishers } from "./blockedPublishers";
import type { InterestVector } from "./interest";
import { rankArticles, type RankParams } from "./rank";
import { findRelatedArticles } from "./related";
import { TOPIC_QUESTIONS } from "../onboarding/topics";

/** Flat list of {tag, label} topic options for the end-of-feed "more of
 *  what you care about" prompt — the same curated vocabulary the
 *  onboarding + settings pickers use, deduped across questions. */
const TOPIC_OPTIONS = TOPIC_QUESTIONS.flatMap((q) => q.options);
import type { EngagementType } from "./engagement";
import type { ManifestEntry } from "./types";
import { useEndlessScroll } from "./useEndlessScroll";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; entries: ManifestEntry[]; generatedAt: number | null }
  | { kind: "stale-schema" }
  | { kind: "offline-empty" };

export interface FeedProps {
  /** Interest vector for client-side re-ranking. When null/empty the feed
   * stays in server base-score order. */
  interest?: InterestVector | null;
  /** Ranking weights — overridable from the Settings drawer. When omitted,
   * `rankArticles` uses its compiled defaults. The Feed re-renders (and
   * thus re-ranks) the moment the parent passes a new value, which is
   * what gives the drawer its "instant" feel. */
  rankParams?: RankParams;
  /** Engagement sink. Undefined when personalization is off (declined /
   * pre-consent) — in that mode no events are recorded. */
  onEngage?: (type: EngagementType, entry: ManifestEntry) => void;
  /** Boost the interest vector toward `tags` (used by the end-of-feed
   *  "what do you want more of?" prompt). The parent persists + updates
   *  its interest state, which re-ranks the feed live. Undefined when
   *  personalization is off, which hides the prompt. */
  onBoostTopics?: (tags: readonly string[]) => void;
  /** Reports the loaded manifest entries up to the shell so the Settings
   *  drawer can render a live re-rank preview. Called whenever the ready
   *  entry set changes (cache hit, then the network refresh). */
  onEntriesLoaded?: (entries: ManifestEntry[]) => void;
}

export function Feed({
  interest,
  rankParams,
  onEngage,
  onBoostTopics,
  onEntriesLoaded,
}: FeedProps) {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [selected, setSelected] = useState<ManifestEntry | null>(null);
  // Optimistic UI state. Ephemeral by design — page reload clears both.
  // Persistence (IndexedDB) is a follow-up; this is the minimum needed
  // for the demo's "buttons do something visible" UX.
  const [liked, setLiked] = useState<Set<number>>(new Set());
  const [hidden, setHidden] = useState<Set<number>>(new Set());
  // Persistent (across reloads) set of publishers the user never wants
  // to see again. Filter is applied alongside the session-only `hidden`
  // set below; `block` from the slide-out adds and closes the pane.
  const { blocked: blockedPublishers, block: blockPublisher } =
    useBlockedPublishers();
  // Tiny toast for the Share button's clipboard-fallback path. Auto-clears
  // ~1.6s after set. Single-string state is enough; no queueing needed.
  const [toast, setToast] = useState<string | null>(null);
  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast((cur) => (cur === msg ? null : cur)), 1600);
  };

  const toggleLike = (entry: ManifestEntry) => {
    setLiked((prev) => {
      const next = new Set(prev);
      if (next.has(entry.id)) next.delete(entry.id);
      else next.add(entry.id);
      return next;
    });
  };

  const hide = (entry: ManifestEntry) => {
    setHidden((prev) => {
      const next = new Set(prev);
      next.add(entry.id);
      return next;
    });
    // If the hidden card is the one open in the modal, close it.
    if (selected?.id === entry.id) setSelected(null);
  };

  const share = async (entry: ManifestEntry) => {
    // Try Web Share first (mobile system share sheet is the right UX).
    // Fall through to clipboard on absence OR on non-cancel failure
    // (Web Share can be present-but-rejecting on insecure contexts,
    // some embedded iframes, or when the call isn't seen as a direct
    // user gesture). AbortError = user dismissed the sheet → no-op.
    if (navigator.share) {
      try {
        await navigator.share({
          title: entry.title,
          text: entry.summary ?? entry.title,
          url: entry.url,
        });
        return;
      } catch (err) {
        if ((err as DOMException)?.name === "AbortError") return;
        // any other error → fall through to clipboard
      }
    }
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(entry.url);
        showToast("Link copied");
      } catch {
        // Clipboard write blocked (no permission, no secure context).
        // Nothing left to try; surface a quiet failure indicator so the
        // user knows the tap registered.
        showToast("Couldn't copy link");
      }
    }
  };

  // Pull the cached articles, then refresh from the network. The SW
  // intercepts the network call and serves SWR — UI gets cache instantly,
  // then a fresher version on the BroadcastChannel "manifest-updated" tick.
  const refresh = async () => {
    const cached = await listArticles();
    if (cached.length > 0) {
      setState({ kind: "ready", entries: cached, generatedAt: null });
    }
    try {
      const m = await loadManifest();
      setState({
        kind: "ready",
        entries: m.articles,
        generatedAt: m.generated_at,
      });
    } catch (err) {
      if (err instanceof StaleSchemaError) {
        setState({ kind: "stale-schema" });
        return;
      }
      if (cached.length === 0) {
        setState({ kind: "offline-empty" });
      }
    }
  };

  // Surface the loaded entries to the shell (for the Settings live-rerank
  // preview). Keyed on the ready entry array identity so it fires on the
  // cache hit and again on the network refresh, not on every render.
  useEffect(() => {
    if (state.kind === "ready") onEntriesLoaded?.(state.entries);
  }, [state, onEntriesLoaded]);

  useEffect(() => {
    void refresh();
    return subscribe(() => {
      // SW says there's a fresher manifest cached. Re-read from IndexedDB
      // (the SW plugin already persisted to the SW cache; we re-fetch the
      // same URL which now serves the fresh body) and re-render.
      void refresh();
    });
  }, []);

  // Client-side re-rank against the interest vector. With no vector this is a
  // stable no-op that preserves the server's base-score order. Then drop
  // anything the user has Hidden this session.
  //
  // Computed defensively (empty array when state.kind !== "ready") so
  // every hook below the early-return checks can be called on every
  // render. Preact tracks hooks by index across renders; calling a hook
  // mid-history (only after state flips to "ready") is undefined
  // behavior. The hook is a no-op on empty input (visibleCount=0,
  // exhausted=true, no observer), so this costs nothing.
  const entries =
    state.kind === "ready"
      ? (interest
          ? rankArticles(state.entries, interest, rankParams)
          : state.entries
        ).filter(
          (e) =>
            !hidden.has(e.id) &&
            !(e.publisher && blockedPublishers.has(e.publisher)),
        )
      : [];

  // Endless-scroll over the (already-ranked, already-filtered) list. The
  // sentinel rides the (N-3)-th visible card; loading more is a state
  // bump, no network call (the manifest is fully in-memory).
  const { visibleCount, triggerRef, triggerIndex, exhausted, loadingMore, loadMore } =
    useEndlessScroll<HTMLLIElement>(entries.length);
  const visibleEntries = entries.slice(0, visibleCount);

  // Stable identities for ArticleDetailView's props so it doesn't diff
  // through unrelated re-renders (toast set/clear, like toggled on a
  // different card, etc.). `related` memoizes on selected.id + the
  // (already-ranked, already-filtered) entries list; `onSelectRelated`
  // wraps the swap in a useCallback that also fires an engagement
  // click for the related-tap.
  //
  // Computed at this level (same place as useEndlessScroll) so they
  // sit above the early returns — Rules of Hooks requires unconditional
  // hook calls per render.
  const related = useMemo(
    () => (selected ? findRelatedArticles(selected, entries) : []),
    // `selected?.id` + `entries` together cover all data
    // `findRelatedArticles` actually reads: `selected.tags` and
    // `selected.url` are write-once from enrich (per BUILD_PLAN §6.1
    // and the proto append-only rule), so a `selected` object-identity
    // churn from a re-rank with the same id can't change the function
    // output. If a future caller adds a `selected.score`-dependent
    // pass to `findRelatedArticles`, ADD that to the deps array — the
    // lint suppression is justified only as long as id covers reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected?.id, entries],
  );
  const onSelectRelated = useCallback(
    (next: ManifestEntry) => {
      onEngage?.("click", next);
      setSelected(next);
    },
    [onEngage],
  );

  // Block-publisher from inside the slide-out: add to the durable set
  // and close the pane (the current article is about to disappear from
  // the feed anyway). Fires a `hide` engagement so the negative signal
  // also flows to personas/rank-velocity — blocking a publisher is at
  // least as strong a negative as hiding a single article.
  const blockFromSelected = useCallback(
    (entry: ManifestEntry) => {
      if (!entry.publisher) return;
      blockPublisher(entry.publisher);
      onEngage?.("hide", entry);
      setSelected(null);
      showToast(`Blocked ${entry.publisher}`);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [blockPublisher, onEngage],
  );

  if (state.kind === "loading") return <p class="loading">Loading feed…</p>;
  if (state.kind === "stale-schema") {
    return (
      <p class="error">
        This client is out of date. Please refresh the page to pick up the new
        manifest format.
      </p>
    );
  }
  if (state.kind === "offline-empty") {
    return (
      <p class="error">
        Offline and nothing cached yet. Reconnect to fetch the latest manifest.
      </p>
    );
  }

  const open = (entry: ManifestEntry) => {
    setSelected(entry);
    onEngage?.("click", entry);
  };

  return (
    <>
      {state.generatedAt ? (
        <p class="generated-at">
          Manifest generated{" "}
          {new Date(state.generatedAt).toLocaleTimeString(undefined, {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </p>
      ) : null}
      {/* aria-busy + aria-live on the list itself so screen-reader users
        * hear "20 more articles loaded" each time a chunk lands — the
        * endless-scroll pattern is known to degrade silently for AT
        * without a live-region story. */}
      <ol class="feed" aria-busy={loadingMore ? "true" : "false"}>
        {visibleEntries.map((e, i) => (
          <li key={e.id} ref={i === triggerIndex ? triggerRef : undefined}>
            <ArticleCard
              entry={e}
              onOpen={open}
              onEngage={onEngage}
              onLike={toggleLike}
              onHide={hide}
              onShare={share}
              liked={liked.has(e.id)}
            />
          </li>
        ))}
      </ol>
      {/* Visually-hidden polite live region — announces chunk loads and
        * the "all caught up" state to screen readers. The visible
        * affordances below are for sighted users. */}
      <p class="sr-only" role="status" aria-live="polite">
        {exhausted && entries.length > 0
          ? "End of feed."
          : loadingMore
            ? "Loading more articles."
            : ""}
      </p>

      {/* End-of-feed zone:
        *  - loading a chunk  → spinner row
        *  - more available   → explicit "Show more stories" button (the
        *                       IntersectionObserver auto-loads too; this
        *                       is the affordance + a fallback where IO
        *                       doesn't fire)
        *  - caught up        → "all caught up" line */}
      {entries.length > 0 ? (
        <div class="feed-end">
          {loadingMore ? (
            <div class="feed-loader" role="status">
              <span class="spinner" aria-hidden="true" />
              <span>Loading more stories…</span>
            </div>
          ) : !exhausted ? (
            <button class="show-more" onClick={() => loadMore()}>
              Show more stories
            </button>
          ) : (
            <p class="end-of-feed">You're all caught up.</p>
          )}
        </div>
      ) : null}

      {/* "What do you want more of?" — only when personalization is on
        * (onBoostTopics provided). Tapping a chip boosts that topic in
        * the on-device interest vector and the feed re-ranks live. */}
      {onBoostTopics && entries.length > 0 ? (
        <section class="topic-prompt" aria-labelledby="topic-prompt-h">
          <h2 id="topic-prompt-h">See more of what you care about</h2>
          <p class="muted">
            Tap a topic to pull more of it toward the top — it tunes your
            feed on this device, nothing leaves your browser.
          </p>
          <ul class="chips">
            {TOPIC_OPTIONS.map((o) => (
              <li key={o.tag}>
                <button
                  class="chip"
                  type="button"
                  onClick={() => {
                    onBoostTopics([o.tag]);
                    showToast(`More ${o.label} coming up`);
                  }}
                >
                  {o.label}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {selected ? (
        <ArticleDetailView
          entry={selected}
          onClose={() => setSelected(null)}
          onEngage={onEngage}
          onLike={toggleLike}
          onHide={hide}
          onShare={share}
          onBlockPublisher={blockFromSelected}
          liked={liked.has(selected.id)}
          related={related}
          onSelectRelated={onSelectRelated}
        />
      ) : null}
      {toast ? (
        <div class="toast" role="status" aria-live="polite">
          {toast}
        </div>
      ) : null}
    </>
  );
}
